/**
 * The assembler's 3D scene: every room on a level, each built ONCE by the room
 * editor's own `buildScene3D` (cached by geometry) and re-placed by transform
 * into one composed `Scene3D`. Walls are never culled here — you look down on
 * the whole level like a dollhouse with the roof off — so only the ceilings
 * keep a cull group (hidden from above, shown from below, as in the editor).
 * The composed scene also carries every room's export records re-placed into
 * the level's plan space, so a level exports to STEP / STL as one model.
 *
 * A doorway between two rooms is drawn by both of them. Composing keeps the
 * first room's door and drops the other's panels — mesh and export solids —
 * when the two boxes describe the same volume (`sameDoorVolume`); each wall
 * keeps its cutout, so the opening stays open from both sides.
 */
import {
  type FloorPlanRoom,
  resolveScene,
  roomAssemblyBounds,
  roomCeilingHeight,
  roomPlacement,
  roomPlacementTransform,
  unionPlanBounds,
} from '@kaiizen/kai-floor-plan';
import { ALL_PLAN_LAYERS, type PlanLayers, showsElement } from '../editor-model';
import type { Vec3 } from '../math3d';
import {
  ALWAYS,
  buildScene3D,
  CEILING_GROUP,
  FLOOR_SLAB_THICKNESS,
  MeshBuilder,
  type Pickable3D,
  type Scene3D,
  type SceneDoor,
  type SceneGadget,
  type SceneLabel,
  type SceneSolid,
  type SceneWall,
} from '../view3d';
import type { AssemblerSelection } from './assemblerState';
import type { AssemblyRoom } from './assemblyModel';
import { appendSelectedRoomOutline, washSelectedFloor } from './selectedRoomHighlight';

/** The placement fields — everything else on a room is geometry. */
const PLACEMENT_KEYS = new Set(['level', 'x', 'y', 'z', 'rotation']);

/** Same geometry (every non-placement field reference-equal)? Placement edits are structurally shared, so this is cheap. */
export function sameRoomGeometry(a: FloorPlanRoom, b: FloorPlanRoom): boolean {
  if (a === b) return true;
  const fieldsA = a as unknown as Record<string, unknown>, fieldsB = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(fieldsA), ...Object.keys(fieldsB)].filter(key => !PLACEMENT_KEYS.has(key)));
  for (const key of keys) if (fieldsA[key] !== fieldsB[key]) return false;
  return true;
}

/** Per-room scene cache keyed by assembly room id, invalidated by a change of geometry, floor depth, or shown layers. */
export class RoomSceneCache {
  private entries = new Map<string, { room: FloorPlanRoom; floorDepth: number; layers: PlanLayers; scene: Scene3D }>();

  get(id: string, room: FloorPlanRoom, floorDepth = FLOOR_SLAB_THICKNESS, layers: PlanLayers = ALL_PLAN_LAYERS): Scene3D {
    const entry = this.entries.get(id);
    if (
      entry && entry.floorDepth === floorDepth && entry.layers.builtIns === layers.builtIns && entry.layers.objects === layers.objects
      && sameRoomGeometry(entry.room, room)
    ) return entry.scene;
    const scene = buildScene3D(room, undefined, { floorDepth, showElement: element => showsElement(layers, element) });
    this.entries.set(id, { room, floorDepth, layers, scene });
    return scene;
  }

  /** Drop rooms no longer in the assembly. */
  retain(ids: Iterable<string>): void {
    const keep = new Set(ids);
    for (const id of this.entries.keys()) if (!keep.has(id)) this.entries.delete(id);
  }
}

/** Two lines within this angle count as parallel: a shared wall drawn from both sides. */
const PARALLEL_COS = Math.cos((5 * Math.PI) / 180);

/** The overlap of two centered spans of half extents `ha` and `hb`, the second offset by `offset`. */
const spanOverlap = (ha: number, hb: number, offset: number): number => Math.max(0, Math.min(ha, offset + hb) - Math.max(-ha, offset - hb));

const boxVolume = (door: SceneDoor): number => 4 * door.halfLength * door.halfWidth * (door.top - door.base);

/**
 * Whether two door boxes describe one doorway: they lie along the same line
 * (parallel, either direction), one holds the other's center, and they share
 * at least half of the smaller volume — a door drawn from both of its rooms,
 * placed so the walls coincide. Boxes crossing at an angle, side by side, or
 * at different heights are different doors.
 */
export function sameDoorVolume(a: SceneDoor, b: SceneDoor): boolean {
  if (Math.abs(a.along.x * b.along.x + a.along.y * b.along.y) < PARALLEL_COS) return false;
  const dx = b.center.x - a.center.x, dy = b.center.y - a.center.y;
  const along = dx * a.along.x + dy * a.along.y;
  const across = -dx * a.along.y + dy * a.along.x;
  const holdsCenter = (Math.abs(along) <= a.halfLength && Math.abs(across) <= a.halfWidth)
    || (Math.abs(along) <= b.halfLength && Math.abs(across) <= b.halfWidth);
  if (!holdsCenter) return false;
  const shared = spanOverlap(a.halfLength, b.halfLength, along) * spanOverlap(a.halfWidth, b.halfWidth, across)
    * Math.max(0, Math.min(a.top, b.top) - Math.max(a.base, b.base));
  const smaller = Math.min(boxVolume(a), boxVolume(b));
  return smaller > 0 && shared >= 0.5 * smaller;
}

/** The middle of the rooms' measurement region: where a composed scene is centered unless its host says otherwise. */
export function assemblySceneCenter(rooms: readonly AssemblyRoom[]): { x: number; y: number } {
  const region = unionPlanBounds(rooms.map(room => roomAssemblyBounds(room.room))) ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { x: (region.minX + region.maxX) / 2, y: (region.minY + region.maxY) / 2 };
}

/**
 * Compose the rooms on one level into a single scene. A selected room wears
 * the move gadget at its center, its footprint traced in the selection blue at
 * floor level and along its wall tops, and a faint blue wash on its floor.
 * `center` is the plan point at the scene's origin; a host that keeps one
 * center while a level is on screen keeps the world still under the camera
 * as rooms move (see AssemblyView3DScene).
 */
export function buildAssemblyScene3D(
  rooms: readonly AssemblyRoom[],
  cache: RoomSceneCache,
  selection?: AssemblerSelection,
  layers: PlanLayers = ALL_PLAN_LAYERS,
  center: { x: number; y: number } = assemblySceneCenter(rooms),
): Scene3D<AssemblerSelection> {
  const mesh = new MeshBuilder();
  const labels: SceneLabel[] = [];
  const pickables: Pickable3D<AssemblerSelection>[] = [];
  // Each room's export records (solids + walls), re-placed into the level's
  // plan space so the composed level serializes to STEP / STL as one model.
  const solids: SceneSolid[] = [];
  const walls: SceneWall[] = [];
  // Every doorway placed so far, in the level's plan space: a later room's
  // door that describes one of these again is a duplicate and is left out.
  const doors: SceneDoor[] = [];
  let ceiling = 0;
  let bbox = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  let gadget: SceneGadget | undefined;

  // Every floor slab reaches down to one plane, a slab's thickness below the
  // lowest floor, so raised rooms leave no gap beneath them.
  const lowestFloor = Math.min(...rooms.map(({ room }) => roomPlacement(room).z));
  for (const { id, name, room } of rooms) {
    const { x, y, z, rotation } = roomPlacement(room);
    const scene = cache.get(id, room, FLOOR_SLAB_THICKNESS + z - lowestFloor, layers);
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians), sin = Math.sin(radians);
    // A room scene is centered on its primary rectangle: world X/Z = plan
    // (px − w/2, py − l/2). Rotating about that center then moving it to the
    // room's placed center is exactly roomPlacementTransform in world terms.
    const dx = x + room.width / 2 - center.x, dz = y + room.length / 2 - center.y;
    const point = ([px, py, pz]: Vec3): Vec3 => [px * cos - pz * sin + dx, py + z, px * sin + pz * cos + dz];
    const normal = ([nx, ny, nz]: Vec3): Vec3 => [nx * cos - nz * sin, ny, nx * sin + nz * cos];
    const toPlan = roomPlacementTransform(room);
    const duplicateDoors = new Set<number>();
    scene.doors.forEach((door, index) => {
      const origin = toPlan({ x: 0, y: 0 }), tip = toPlan(door.along);
      const placed: SceneDoor = {
        ...door,
        center: toPlan(door.center),
        along: { x: tip.x - origin.x, y: tip.y - origin.y },
        base: door.base + z,
        top: door.top + z,
      };
      if (doors.some(other => sameDoorVolume(other, placed))) duplicateDoors.add(index);
      else doors.push(placed);
    });
    const keep = (door: number | undefined): boolean => door === undefined || !duplicateDoors.has(door);
    const selected = selection?.kind === 'room' && selection.id === id;
    // The room's center, mid-height: where the selected room's gadget stands.
    const middle = point([0, roomCeilingHeight(room) / 2, 0]);
    // Walls (groups 0–3) always show; the ceiling keeps the single cull group 0.
    mesh.appendTransformed(
      scene.mesh,
      { point, normal, ...(selected && { color: washSelectedFloor(z) }) },
      group => group === CEILING_GROUP ? 0 : ALWAYS,
      surface => keep(surface.door),
    );
    if (selected) appendSelectedRoomOutline(mesh, room, center, z);
    // One label per room, at its floor center, or its middle while selected;
    // the rooms' own object labels would be a wall of text at this altitude.
    labels.push({ text: name, pos: selected ? middle : point([0, 0, 0]), id });

    for (const solid of scene.solids) {
      if (!keep(solid.door)) continue;
      solids.push({ ...solid, rings: solid.rings.map(ring => ring.map(toPlan)), base: solid.base + z, top: solid.top + z });
    }
    for (const wall of scene.walls) {
      walls.push({
        ...wall,
        name: `${name} · ${wall.name}`,
        base: wall.base + z,
        top: wall.top + z,
        leaves: wall.leaves.map(leaf => ({
          ...leaf,
          innerStart: toPlan(leaf.innerStart),
          innerEnd: toPlan(leaf.innerEnd),
          outerStart: toPlan(leaf.outerStart),
          outerEnd: toPlan(leaf.outerEnd),
        })),
      });
    }
    const roomBounds = roomAssemblyBounds(room);
    const top = z + roomCeilingHeight(room);
    ceiling = Math.max(ceiling, top);
    // The selected room's gadget stands at its center, mid-height; a room moves
    // in plan and in floor elevation.
    if (selected) gadget = { center: middle, vertical: true };
    const picked: AssemblerSelection = { kind: 'room', id };
    // Solid target: the room's placed extent as a box (over-approximates a rotated room; nearest hit wins).
    pickables.push({
      sel: picked,
      group: null,
      kind: 'box',
      min: [roomBounds.minX - center.x, z, roomBounds.minY - center.y],
      max: [roomBounds.maxX - center.x, top, roomBounds.maxY - center.y],
    });
    // Floor fallback: the exact placed footprint.
    pickables.push({
      sel: picked,
      group: null,
      kind: 'floor',
      rings: resolveScene(room).roomSolid.flat().map(ring =>
        ring.map(p => {
          const q = toPlan(p);
          return { x: q.x - center.x, z: q.y - center.y };
        })
      ),
    });
    const corners = [
      toPlan({ x: scene.bbox.minX, y: scene.bbox.minY }),
      toPlan({ x: scene.bbox.maxX, y: scene.bbox.minY }),
      toPlan({ x: scene.bbox.maxX, y: scene.bbox.maxY }),
      toPlan({ x: scene.bbox.minX, y: scene.bbox.maxY }),
    ];
    for (const c of corners) {
      bbox = { minX: Math.min(bbox.minX, c.x), minY: Math.min(bbox.minY, c.y), maxX: Math.max(bbox.maxX, c.x), maxY: Math.max(bbox.maxY, c.y) };
    }
  }
  if (!rooms.length) bbox = { minX: center.x, minY: center.y, maxX: center.x, maxY: center.y };

  return {
    mesh,
    solids,
    walls,
    doors,
    bbox,
    center,
    ceiling,
    // One cull group: the ceilings, hidden while the eye is above them.
    wallNormals: [[0, -1, 0]],
    wallPoints: [[0, ceiling, 0]],
    labels,
    pickables,
    gadget,
    selectionCenter: gadget?.center,
  };
}
